<template>
  <app-details-sidebar
    :sidebar-title="getTitle"
    :show-sidebar="getShowSidebar"
    :show-footer="getShowFooter"
    @hideSidebar="hideSidebar($event)"
    @editItem="editItem"
    @deleteItem="deleteItem"
  >

    <template v-if="detailedItem"
              v-slot:detailsHeader>
      <p v-if="detailedItem.isRentalIncomeProperty">
        Rental Income Property
      </p>
    </template>

    <template v-if="detailedItem"
              v-slot:detailsContent>

      <app-delete-dialog :value="deleteDialog"
                         @delete="deleteRealEstateItem(detailedItem.id)"
                         @close="closeDialog">
        Are you sure you want to delete this real estate?
      </app-delete-dialog>

      <div :class="$forms.detailsTable">
        <div :class="$forms.title">Main Information</div>
        <div :class="$forms.main">
          <div :class="$forms.mainRow">
            <h6>Location</h6>
            <p>{{ getLocation }}</p>
          </div>

          <div :class="$forms.mainRow">
            <h6>Property Type</h6>
            <p>{{ getTitle }}</p>
          </div>

          <div :class="$forms.mainRow">
            <h6>Year Purchased</h6>
            <p>{{ detailedItem.yearPurchased }}</p>
          </div>

          <div :class="$forms.mainRow">
            <h6>Purchase Price</h6>
            <p>{{ detailedItem.purchasePrice | currency }}</p>
          </div>

          <div :class="$forms.mainRow">
            <h6>Monthly Payment(s)</h6>
            <p>{{ detailedItem.monthlyPayment | currency }}</p>
          </div>

          <div :class="$forms.mainRow">
            <h6>Year Refinanced</h6>
            <p>{{ detailedItem.yearRefinanced }}</p>
          </div>

          <div :class="$forms.mainRow">
            <h6>Refinanced Amount</h6>
            <p>{{ detailedItem.refinancedAmount | currency }}</p>
          </div>

          <div :class="$forms.mainRow">
            <h6>Current Value</h6>
            <p>{{ detailedItem.currentValue | currency }}</p>
          </div>

          <div :class="$forms.mainRow">
            <h6>Balance Owed</h6>
            <p>{{ detailedItem.balanceOwed | currency }}</p>
          </div>
        </div>
      </div>

      <div v-if="detailedItem.isRentalIncomeProperty"
           :class="$forms.detailsTable">
        <div :class="$forms.title">Rental Information</div>
        <div :class="$forms.main">
          <div :class="$forms.mainRow">
            <h6>Rental Income</h6>
            <p>{{ detailedItem.rentalIncome | currency }}</p>
          </div>

          <div :class="$forms.mainRow">
            <h6>Insurance(s)</h6>
            <p>{{ detailedItem.insurance | currency }}</p>
          </div>

          <div :class="$forms.mainRow">
            <h6>Management Fees / Commission</h6>
            <p>{{ detailedItem.managementFees | currency }}</p>
          </div>

          <div :class="$forms.mainRow">
            <h6>Cleaning / Maintenance / Repairs</h6>
            <p>{{ detailedItem.maintenance | currency }}</p>
          </div>

          <div :class="$forms.mainRow">
            <h6>Property Tax / Legal / Admin Fees</h6>
            <p>{{ detailedItem.propertyTax | currency }}</p>
          </div>
        </div>
      </div>
    </template>

    <template v-if="detailedItem"
              v-slot:detailsFooter>
      <div :class="$style.rentalIncomeResult">
        <div>
          <h6>Rental Income - Total Expenses</h6>
          <p :class="$style.note">* Rental Income - Total Expenses = Net Rental Income</p>
        </div>

        <span :class="$style.netRentalIncome">{{ detailedItem.netRentalIncome | currency }}</span>
      </div>

    </template>
  </app-details-sidebar>
</template>

<script>
import currency from 'currency.js';
import { mapActions, mapGetters } from 'vuex';
import { RESIDENCE_TYPES } from '@/constants';

export default {
  props: {
    showSidebar: {
      type: Boolean,
      default: false,
    },
    detailedItem: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      deleteDialog: false,
      location: '',
    };
  },
  methods: {
    ...mapActions([
      'deleteRealEstate',
      'countiesRequest',
    ]),
    hideSidebar(e) {
      this.$emit('hideSidebar', e);
    },
    editItem() {
      this.hideSidebar();
      this.$emit('editItem', this.detailedItem, this.detailedItem.id);
    },
    deleteItem() {
      this.deleteDialog = true;
    },
    closeDialog() {
      this.deleteDialog = false;
    },
    /* Delete job item by click at the Delete button in the dialog */
    deleteRealEstateItem(id) {
      this.deleteRealEstate({
        id,
        userId: this.getUserId,
      }).then(() => {
        this.closeDialog();
        this.hideSidebar();
      });
    },
    setLocation() {
      this.countiesRequest(this.detailedItem.stateAbbreviation)
        .then(() => {
          const county = this.getCountiesOfState
            .find(item => item.id === this.detailedItem.countyId);

          this.location = `${this.detailedItem.stateAbbreviation}, ${this.detailedItem.city}, ${county.name}`;
        });
    },
  },
  computed: {
    ...mapGetters([
      'getUserId',
      'getCountiesOfState',
    ]),
    getResidenceTypes() {
      return RESIDENCE_TYPES.map(type => type.textValue);
    },
    getShowSidebar() {
      if (this.showSidebar) {
        this.setLocation();
      }
      return this.showSidebar;
    },
    getTitle() {
      if (this.detailedItem && this.detailedItem.residenceType) {
        return RESIDENCE_TYPES.find(type => type
          .code === this.detailedItem.residenceType).textValue;
      }

      return '';
    },
    getLocation() {
      return this.location;
    },
    getShowFooter() {
      if (this.detailedItem) {
        return this.detailedItem.isRentalIncomeProperty;
      }

      return false;
    },
  },
  directives: {
    currency,
  },
};
</script>

<style src="@/styles/forms.scss" lang="scss" module="$forms"></style>

<style lang="scss" module>
@import "@/styles/variables.scss";

.rentalIncomeResult {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .netRentalIncome {
    padding: 0;
  }
}

.note {
  color: $light-text-color;
  font-size: 1.3rem;
  line-height: 2.1rem;
  font-style: italic;
  margin-bottom: 0;
}
</style>
